import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Row, Col, Tag, Tooltip, message } from "antd";
import AgGridComponent from "../../../AG-grid/AgGridComponent";
import LoadableComp from "../../../commonComponents/LoadableComp/LoadableComp";
import MondayButton from "../../../commonComponents/MondayButton/MondayButton";
import GlobalPopupKonfirm from "../../../commonComponents/GlobalPopupKonfirm";
import { API } from "../../../../helpers/API/amplifyApi";
import { IconNdrysho, DeleteIcon } from "../../../../assets/icons";
import dayjsTZ from "../../../../utils/dayjs";
import { useAuth } from "../../../../authentication/authHelpers";
import { isAdminRole } from "../../../Sidebar/ensureRouteAccess";
import PolicyModal from "./PolicyModal";
import "./rotacioni_turneve.scss";

const ruleModeLabel = (m) => {
  switch (m) {
    case "rotate":
      return "Rotacion";
    case "fixed_morning":
      return "Vetëm mëngjes";
    case "fixed_afternoon":
      return "Vetëm pasdite";
    default:
      return "Përjashtuar";
  }
};

function RotacioniTurneve() {
  const auth = useAuth();
  const userRole =
    auth?.user?.userRole || auth?.userAccess?.[0]?.userRole || "";

  const allowed = isAdminRole(userRole);

  const [policies, setPolicies] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadAll = async () => {
    try {
      const [pol, deps, shs, emps] = await Promise.all([
        API.get("shiftRotationPolicies", "/shiftRotationPolicies"),
        API.get("departments", "/departments"),
        API.get("shifts", "/shifts"),
        API.get("employees", "/employees"),
      ]);
      setPolicies(pol || []);
      setDepartments(deps || []);
      setShifts(shs || []);
      setEmployees(emps || []);
    } catch (e) {
      console.error(e);
      message.error("Nuk u ngarkuan dot të dhënat.");
      setPolicies([]);
    }
  };

  useEffect(() => {
    if (!allowed) return undefined;
    loadAll();
    return undefined;
  }, [allowed]);

  const shiftNameById = useMemo(() => {
    const out = {};
    shifts.forEach((s) => (out[s.shiftId] = s.shiftName));
    return out;
  }, [shifts]);

  const onDelete = async (row) => {
    try {
      await API.del(
        "shiftRotationPolicies",
        `/shiftRotationPolicies/${row.policyId}`
      );
      message.success("Politika u fshi.");
      loadAll();
    } catch (e) {
      console.error(e);
      message.error("Nuk u fshi dot politika.");
    }
  };

  const columnDefs = [
    {
      headerName: "Departamenti",
      field: "departmentName",
      headerTooltip: "Departamenti",
      flex: 2,
      minWidth: 200,
      width: 220,
      suppressSizeToFit: true,
      mobile: true,
      isCollapseTitle: true,
    },
    {
      headerName: "Turni mëngjes",
      field: "shiftIds.morningShiftId",
      headerTooltip: "Turni mëngjes",
      flex: 1.4,
      minWidth: 170,
      width: 180,
      suppressSizeToFit: true,
      mobile: true,
      valueGetter: ({ data }) =>
        shiftNameById[data?.shiftIds?.morningShiftId] || "—",
    },
    {
      headerName: "Turni pasdite",
      field: "shiftIds.afternoonShiftId",
      headerTooltip: "Turni pasdite",
      flex: 1.4,
      minWidth: 170,
      width: 180,
      suppressSizeToFit: true,
      mobile: true,
      valueGetter: ({ data }) =>
        shiftNameById[data?.shiftIds?.afternoonShiftId] || "—",
    },
    {
      headerName: "Rolet",
      field: "roleRules",
      headerTooltip: "Rolet",
      flex: 3,
      minWidth: 260,
      width: 280,
      suppressSizeToFit: true,
      mobile: true,
      autoHeight: true,
      wrapText: true,
      cellStyle: { lineHeight: "22px", paddingTop: 6, paddingBottom: 6 },
      cellRenderer: ({ data }) => (
        <div
          className="rotacioni-role-tags"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            alignItems: "center",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          {(data?.roleRules || [])
            .filter((r) => r?.mode && r.mode !== "excluded")
            .map((r) => {
            const teams = r.teamQuotas ? Object.keys(r.teamQuotas) : [];
            const perTeam = teams
              .map((t) => {
                const q = r.teamQuotas[t] || {};
                const tName = t === "_unteamed" ? "Pa ekip" : t.replace("Flex ", "");
                return `${tName}: ${Number(q.minSeniors || 0)}sr+${Number(q.minJuniors || 0)}jr`;
              })
              .join(" · ");
            const summary = perTeam
              ? `${ruleModeLabel(r.mode)} • ${perTeam} • ${r.minYearsExperience}+ vite`
              : `${ruleModeLabel(r.mode)} • ${r.minSeniorsAfternoonWeek ?? r.minPerAfternoonWeek ?? 0}sr+${r.minJuniorsAfternoonWeek ?? 0}jr/javë • ${r.minYearsExperience}+ vite`;
            return (
              <Tooltip key={r.roleId} title={summary}>
                <Tag
                  color={r.mode === "rotate" ? "blue" : "default"}
                  style={{ marginInlineEnd: 0, maxWidth: "100%" }}
                >
                  {r.roleName}: {ruleModeLabel(r.mode)}
                </Tag>
              </Tooltip>
            );
          })}
        </div>
      ),
    },
    {
      headerName: "Statusi",
      field: "status",
      headerTooltip: "Statusi",
      flex: 0.8,
      minWidth: 120,
      width: 130,
      suppressSizeToFit: true,
      mobile: true,
      cellRenderer: ({ data }) => (
        <Tag color={data?.status === "active" ? "green" : "orange"}>
          {data?.status === "active" ? "Aktiv" : "Pauzuar"}
        </Tag>
      ),
    },
    {
      headerName: "Përditësuar",
      field: "updatedAt",
      headerTooltip: "Përditësuar",
      flex: 1.2,
      minWidth: 160,
      width: 170,
      suppressSizeToFit: true,
      mobile: true,
      cellRenderer: ({ data }) =>
        data?.updatedAt ? dayjsTZ(data.updatedAt).format("DD/MM/YYYY HH:mm") : "—",
    },
    {
      headerName: "Veprime",
      field: "actions",
      colId: "actions",
      headerTooltip: "Veprime",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      flex: 0,
      pinned: "right",
      sortable: false,
      filter: false,
      resizable: false,
      suppressSizeToFit: true,
      cellClass: "rotacioni-actions-cell",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible",
      },
      cellRenderer: ({ data }) => (
        <div className="rotacioni-actions">
          <Tooltip title="Përditëso">
            <button
              type="button"
              className="rotacioni-action-btn rotacioni-action-btn--edit icon-ndrysho"
              aria-label="Përditëso"
              onClick={(e) => {
                e.stopPropagation();
                setEditing(data);
                setModalOpen(true);
              }}
            >
              <IconNdrysho width={14} height={14} fill="#fff" />
            </button>
          </Tooltip>
          <GlobalPopupKonfirm
            Button={
              <Tooltip title="Fshi">
                <button
                  type="button"
                  className="rotacioni-action-btn rotacioni-action-btn--delete icon-fshi"
                  aria-label="Fshi"
                >
                  <DeleteIcon width={14} height={14} fill="#fff" />
                </button>
              </Tooltip>
            }
            title="A jeni i sigurt që doni ta fshini këtë politikë?"
            onConfirm={() => onDelete(data)}
            okText="Po"
            cancelText="Jo"
          />
        </div>
      ),
    },
  ];

  if (!allowed) {
    return <Navigate to="/konfigurimet" replace />;
  }

  return (
    <LoadableComp loading={policies === null}>
      <div className="menaxhimi-orarit">
        <Row style={{ marginBottom: 10 }}>
          <Col span={24} style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <MondayButton
              className="mondayButtonGreen"
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              + Krijo politikë
            </MondayButton>
          </Col>
        </Row>
        <AgGridComponent
          rowData={policies || []}
          columnDefs={columnDefs}
          paginationPageSize={20}
          rowSelection="single"
          headerProps={{
            items: {
              search: { placeholder: "Kërko departament..." },
            },
            exportTitle: "Rotacioni i turneve",
          }}
        />
      </div>

      {modalOpen ? (
        <PolicyModal
          open={modalOpen}
          initial={editing}
          departments={departments}
          shifts={shifts}
          employees={employees}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          onSaved={loadAll}
        />
      ) : null}
    </LoadableComp>
  );
}

export default RotacioniTurneve;
